<template>
  <div class="chat__wrap">
    <div class="chat__mid">
      <!--채팅내용 스크롤-->
      <div class="chat__mid__scroll-body">
        <!-- 고정 안내 -->
        <div class="chat__get">
          <div class="chat__profile">
            <img src="@/assets/img/noimg_profile.png" alt="프로필" />
          </div>
          <div class="chat__info-area">[문의 및 요청 시 안내]</div>
          <div class="chat__box">
            신고하신 취약점에 대한 문의 사항과 추가 자료를 등록하실 수 있습니다.
            (자유 형식 / 최대 100,000자)
            <br />
            <span class="u-color-red"
              >※ 첨부 가능 확장자: png, jpg, gif, pdf, xlsx, xls, zip, tar, 7z
              (최대 150MB)</span
            >
          </div>
        </div>

        <!-- 동적 피드백 목록 -->
        <template v-if="messagesByDate.length">
          <div
            v-for="(data, idx) in messagesByDate"
            :key="data.feedbackId || 'date-' + idx"
          >
            <!-- 날짜 라벨 -->
            <div v-if="data.isDateLabel" class="chat__date-area">
              <span>{{ formatDateLabel(data.date) }}</span>
            </div>

            <!-- 일반 메시지 -->
            <div
              v-else
              :class="
                data.feedbackType === 'INQUIRY' ? 'chat__send' : 'chat__get'
              "
            >
              <template v-if="data.feedbackType !== 'INQUIRY'">
                <div class="chat__profile">
                  <img src="@/assets/img/noimg_profile.png" alt="프로필" />
                </div>
              </template>

              <div class="chat__info-area">{{ formatDate(data.regDate) }}</div>
              <div v-if="data.content" class="chat__box">
                {{ data.content }}
              </div>

              <div v-if="data.image" class="chat__img">
                <img
                  :src="imageUrls[data.feedbackId] || data.image"
                  alt="image"
                />
              </div>

              <div v-if="data.attach" class="chat__box">
                <a
                  class="link-primary"
                  @click.prevent="downloadFile(data, 'ATTACH')"
                >
                  {{ data.fileRealName }}
                </a>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 메시지 입력 -->
      <div class="chat__bottom">
        <div class="chat__bottom__btns">
          <!-- 파일 업로드 -->
          <button
            type="button"
            class="btn btn--white_light btn--line btn--icon btn_file"
            @click="triggerFileUpload('file')"
          >
            <i class="svg_icon icon_file"></i>
          </button>

          <!-- 이미지 업로드 -->
          <button
            type="button"
            class="btn btn--white_light btn--line btn--icon btn_photo"
            @click="triggerFileUpload('image')"
          >
            <i class="svg_icon icon_photo"></i>
          </button>

          <!-- 숨겨진 input -->
          <input
            type="file"
            ref="fileInput"
            multiple
            style="display: none"
            @change="handleFileChange"
            :accept="acceptTypes"
          />
        </div>

        <div class="chat__bottom__input__area">
          <textarea
            placeholder="내용 입력"
            class="chat__textarea"
            v-model="content"
            maxlength="100000"
          ></textarea>
          <p class="chat__textarea-txt">
            {{ content.length.toLocaleString() }}/100,000
          </p>
        </div>

        <button class="btn btn--blue btn_send" @click="addFeedback">
          등록하기
        </button>
      </div>

      <!-- 선택한 첨부파일/이미지 리스트 -->
      <div
        v-show="attachedItems.length"
        v-for="item in attachedItems"
        :key="item.type + '-' + item.index"
        class="chat__file-item"
      >
        {{ item.type === "file" ? "첨부 파일" : "이미지" }} :
        <span class="chat__file-name ellipsis">{{ item.name }}</span>
        <span class="chat__file-size">
          Size : ({{ formatFileSize(item.size) }})
        </span>
        <button
          type="button"
          class="btn_close"
          @click="
            item.type === 'file'
              ? removeFile(item.index)
              : removeImage(item.index)
          "
        >
          <i class="svg_icon icon_del" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, reactive, watch } from "vue";
import { useStore } from "vuex";
import dc from "@/component/DataChk.js";

export default {
  props: {
    modelValue: { type: Object, required: true },
    feedbackInfo: { type: Array, required: false },
  },
  setup(props, { emit }) {
    const axios = inject("axios");
    const store = useStore();
    const showLoading = () => store.dispatch("loading/show");
    const hideLoading = () => store.dispatch("loading/hide");

    // data 정의
    const reportInfo = reactive(props.modelValue.reportInfo);
    const content = ref("");
    const attachedFiles = ref([]);
    const attachedImages = ref([]);
    const imageUrls = reactive({});
    const fileInput = ref(null);
    const acceptTypes = ref("");
    const uploadType = ref("");
    const allowImageExt = ".png,.jpg,.jpeg,.gif";
    const allowFileExt = ".pdf,.xlsx,.xls,.zip,.tar,.7z";

    // methods 정의
    const triggerFileUpload = (type) => {
      uploadType.value = type;
      acceptTypes.value = type === "image" ? allowImageExt : allowFileExt;
      setTimeout(() => fileInput.value?.click(), 0);
    };
    const handleFileChange = (e) => {
      const files = [...(e.target.files || [])];
      if (uploadType.value === "image") attachedImages.value.push(...files);
      else attachedFiles.value.push(...files);
      e.target.value = "";
    };
    const removeFile = (index) => attachedFiles.value.splice(index, 1);
    const removeImage = (index) => attachedImages.value.splice(index, 1);

    const isValidFeedbackInfo = () => {
      if (
        !dc.nullChk(content.value) &&
        !dc.nullChk(attachedImages.value) &&
        !dc.nullChk(attachedFiles.value)
      ) {
        alert("문의 내용을 입력하십시오.");
        return false;
      }
      return true;
    };

    const addFeedback = () => {
      if (!isValidFeedbackInfo()) {
        return;
      }

      const vo = {
        content: content.value,
        makr: reportInfo.makr,
        swNm: reportInfo.swNm,
        reportDate: reportInfo.regDate,
        kve: reportInfo.kve,
        vulId: reportInfo.id,
      };
      const formData = new FormData();
      formData.append("vo", JSON.stringify(vo));
      formData.append("vulId", reportInfo.id);
      attachedImages.value.forEach((img) => formData.append("imgFiles", img));
      attachedFiles.value.forEach((file) =>
        formData.append("attachFiles", file)
      );
      showLoading();
      axios
        .post("/report/pr/records/feedback/add", formData)
        .then((res) => {
          if (res.data.resType === "RES_OK") {
            content.value = "";
            attachedFiles.value = [];
            attachedImages.value = [];
            emit("refresh-feedback");
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          hideLoading();
        });
    };
    const formatFileSize = (bytes) => `${bytes?.toLocaleString() || 0} Byte`;
    const formatDate = (d) => (d ? new Date(d).toLocaleString() : "");
    const downloadFile = async (data, type) => {
      if (!data?.attach) {
        return;
      }

      try {
        const res = await fetchFileBlob(data.feedbackId, type);

        if (!res) {
          return;
        }

        const blob = new Blob([res.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = data.fileRealName || "download";
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error(error);
      }
    };
    const showImage = async (data) => {
      if (!data?.image || !data?.feedbackId) {
        return;
      }

      try {
        const res = await fetchFileBlob(data.feedbackId, "IMG");

        if (!res) {
          return;
        }

        const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);

        // 이전 이미지 URL 정리
        if (imageUrls[data.feedbackId]) {
          URL.revokeObjectURL(imageUrls[data.feedbackId]);
        }
        imageUrls[data.feedbackId] = URL.createObjectURL(blob);
      } catch (error) {
        console.error(error);
      }
    };
    const fetchFileBlob = async (feedbackId, type) => {
      showLoading();
      try {
        const res = await axios.post(
          "/report/pr/file/feedback-file/get",
          { id: feedbackId, type },
          { responseType: "blob" }
        );

        return res;
      } catch (error) {
        console.error(error);

        return null;
      } finally {
        hideLoading();
      }
    };
    const formatDateLabel = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return `${date.getFullYear()}. ${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}. ${String(date.getDate()).padStart(2, "0")}`;
    };

    // computed 정의
    const messages = computed(() => {
      const arr = Array.isArray(props.feedbackInfo)
        ? [...props.feedbackInfo]
        : [];
      return arr
        .filter((x) => x)
        .sort((a, b) => new Date(a.regDate) - new Date(b.regDate));
    });
    const messagesByDate = computed(() => {
      const arr = messages.value;
      const grouped = [];
      let lastDate = null;

      arr.forEach((msg) => {
        const dateStr = msg.regDate
          ? new Date(msg.regDate).toISOString().slice(0, 10)
          : null;
        if (dateStr !== lastDate) {
          grouped.push({ isDateLabel: true, date: dateStr });
          lastDate = dateStr;
        }
        grouped.push(msg);
      });

      return grouped;
    });
    const attachedItems = computed(() => {
      const files = attachedFiles.value.map((data, i) => ({
        type: "file",
        name: data.name,
        size: data.size,
        index: i,
      }));
      const images = attachedImages.value.map((data, i) => ({
        type: "image",
        name: data.name,
        size: data.size,
        index: i,
      }));
      return [...files, ...images];
    });

    // watch 정의
    watch(
      messages,
      (list) => {
        (list || []).forEach((data) => {
          if (data?.image) showImage(data);
        });
      },
      { immediate: true }
    );

    // html에서 사용될 데이터 정의
    return {
      content,
      attachedFiles,
      attachedImages,
      fileInput,
      acceptTypes,
      triggerFileUpload,
      handleFileChange,
      removeFile,
      removeImage,
      addFeedback,
      formatFileSize,
      formatDate,
      messagesByDate,
      formatDateLabel,
      downloadFile,
      imageUrls,
      showImage,
      attachedItems,
    };
  },
};
</script>
